import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  GraduationCap, Users, BookOpen, CreditCard, Receipt, BarChart3,
  Plus, Search, MoreVertical, Trash2, DollarSign, TrendingUp,
  UserPlus, ClipboardList, Wallet, AlertCircle, CheckCircle2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(n || 0);
const STATUS_COLORS = { active: 'bg-emerald-100 text-emerald-700', inactive: 'bg-slate-100 text-slate-700', graduated: 'bg-blue-100 text-blue-700', suspended: 'bg-red-100 text-red-700', withdrawn: 'bg-amber-100 text-amber-700' };
const INV_COLORS = { pending: 'bg-amber-100 text-amber-700', partial: 'bg-blue-100 text-blue-700', paid: 'bg-emerald-100 text-emerald-700', overdue: 'bg-red-100 text-red-700' };
const PROVINCES = ["Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo", "Mpumalanga", "North West", "Northern Cape", "Western Cape"];

export const StudentBillingPage = () => {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Dialogs
  const [showStudent, setShowStudent] = useState(false);
  const [showCourse, setShowCourse] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [editStudent, setEditStudent] = useState(null);

  // Forms
  const emptyStu = { first_name: '', last_name: '', email: '', phone: '', id_number: '', date_of_birth: '', gender: '', nationality: 'South African', province: '', department: '', program: '', enrollment_status: 'active', enrollment_date: '', guardian_name: '', guardian_phone: '', guardian_email: '', guardian_relationship: '' };
  const emptyCrs = { code: '', name: '', department: '', description: '', duration_months: 12, credits: 0, fee_amount: 0, registration_fee: 0, material_fee: 0, max_students: 0 };
  const emptyEnr = { student_id: '', course_id: '', enrollment_date: new Date().toISOString().slice(0, 10), scholarship_amount: 0, discount_percent: 0 };
  const emptyPay = { invoice_id: '', student_id: '', amount: 0, method: 'eft', reference: '', payment_date: new Date().toISOString().slice(0, 10) };
  const [stuForm, setStuForm] = useState(emptyStu);
  const [crsForm, setCrsForm] = useState(emptyCrs);
  const [enrForm, setEnrForm] = useState(emptyEnr);
  const [payForm, setPayForm] = useState(emptyPay);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, s, c, e, i, p] = await Promise.all([
        axios.get(`${API}/student-billing/dashboard`),
        axios.get(`${API}/student-billing/students`),
        axios.get(`${API}/student-billing/courses`),
        axios.get(`${API}/student-billing/enrollments`),
        axios.get(`${API}/student-billing/invoices`),
        axios.get(`${API}/student-billing/payments`)
      ]);
      setDashboard(d.data); setStudents(s.data); setCourses(c.data);
      setEnrollments(e.data); setInvoices(i.data); setPayments(p.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreateStudent = async () => {
    if (!stuForm.first_name || !stuForm.last_name) { toast.error('Name required'); return; }
    try {
      if (editStudent) { await axios.put(`${API}/student-billing/students/${editStudent.id}`, stuForm); toast.success('Student updated'); }
      else { await axios.post(`${API}/student-billing/students`, stuForm); toast.success('Student added'); }
      setShowStudent(false); setStuForm(emptyStu); setEditStudent(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Delete student?')) return;
    try { await axios.delete(`${API}/student-billing/students/${id}`); toast.success('Deleted'); loadAll(); } catch { toast.error('Failed'); }
  };

  const handleCreateCourse = async () => {
    if (!crsForm.code || !crsForm.name) { toast.error('Code and name required'); return; }
    try { await axios.post(`${API}/student-billing/courses`, crsForm); toast.success('Course created'); setShowCourse(false); setCrsForm(emptyCrs); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEnroll = async () => {
    if (!enrForm.student_id || !enrForm.course_id) { toast.error('Select student and course'); return; }
    try { await axios.post(`${API}/student-billing/enrollments`, enrForm); toast.success('Student enrolled & invoice generated'); setShowEnroll(false); setEnrForm(emptyEnr); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handlePayment = async () => {
    if (!payForm.invoice_id || !payForm.amount) { toast.error('Select invoice and enter amount'); return; }
    try { await axios.post(`${API}/student-billing/payments`, payForm); toast.success('Payment recorded'); setShowPayment(false); setPayForm(emptyPay); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const stats = dashboard?.stats || {};

  if (loading) return <div className="flex items-center justify-center h-96 animate-pulse">Loading...</div>;

  // ===== DASHBOARD =====
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { l: 'Students', v: stats.active_students, icon: Users, c: 'bg-blue-100 text-blue-600' },
          { l: 'Courses', v: stats.total_courses, icon: BookOpen, c: 'bg-purple-100 text-purple-600' },
          { l: 'Total Invoiced', v: fmt(stats.total_invoiced), icon: Receipt, c: 'bg-amber-100 text-amber-600', mono: true },
          { l: 'Collected', v: fmt(stats.total_collected), icon: Wallet, c: 'bg-emerald-100 text-emerald-600', mono: true },
          { l: 'Outstanding', v: fmt(stats.total_outstanding), icon: AlertCircle, c: 'bg-red-100 text-red-600', mono: true },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-4"><div className="flex items-center gap-3"><div className={`h-10 w-10 rounded-lg ${s.c} flex items-center justify-center`}><s.icon className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">{s.l}</p><p className={`text-xl font-bold ${s.mono ? 'font-mono' : ''}`}>{s.v}</p></div></div></CardContent></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-5 text-center"><p className="text-sm text-muted-foreground">Collection Rate</p><p className="text-4xl font-bold font-mono mt-1" style={{ color: stats.collection_rate >= 70 ? '#16a34a' : stats.collection_rate >= 40 ? '#d97706' : '#dc2626' }}>{stats.collection_rate || 0}%</p></CardContent></Card>
        <Card><CardContent className="p-5 text-center"><p className="text-sm text-muted-foreground">Active Enrollments</p><p className="text-4xl font-bold font-mono mt-1">{stats.active_enrollments || 0}</p></CardContent></Card>
        <Card><CardContent className="p-5 text-center"><p className="text-sm text-muted-foreground">Pending Invoices</p><p className="text-4xl font-bold font-mono mt-1 text-amber-600">{stats.pending_invoices || 0}</p></CardContent></Card>
      </div>
      {Object.keys(dashboard?.revenue_by_course || {}).length > 0 && (
        <Card><CardHeader><CardTitle className="text-base">Revenue by Course</CardTitle></CardHeader><CardContent>
          <div className="space-y-3">{Object.entries(dashboard.revenue_by_course).map(([name, data]) => (
            <div key={name} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="font-medium text-sm">{name}</span>
              <div className="flex gap-4 text-sm font-mono"><span>Invoiced: {fmt(data.invoiced)}</span><span className="text-emerald-600">Collected: {fmt(data.collected)}</span></div>
            </div>
          ))}</div>
        </CardContent></Card>
      )}
    </div>
  );

  // ===== STUDENTS =====
  const filteredStudents = students.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.full_name?.toLowerCase().includes(q) || s.student_number?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
  });

  const renderStudents = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search students..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="student-search" /></div>
        <Button onClick={() => { setStuForm(emptyStu); setEditStudent(null); setShowStudent(true); }} data-testid="add-student-btn"><UserPlus className="h-4 w-4 mr-2" />Add Student</Button>
      </div>
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow><TableHead>Student #</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Department</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
        <TableBody>
          {filteredStudents.map(s => (
            <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
              <TableCell className="font-mono text-sm">{s.student_number}</TableCell>
              <TableCell className="font-medium">{s.full_name}</TableCell>
              <TableCell className="text-sm">{s.email || '-'}</TableCell>
              <TableCell className="text-sm">{s.department || '-'}</TableCell>
              <TableCell><Badge className={STATUS_COLORS[s.enrollment_status] || 'bg-slate-100'}>{s.enrollment_status}</Badge></TableCell>
              <TableCell className="text-right font-mono"><span className={s.outstanding_balance > 0 ? 'text-red-600' : 'text-emerald-600'}>{fmt(s.outstanding_balance)}</span></TableCell>
              <TableCell>
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setEditStudent(s); setStuForm(s); setShowStudent(true); }}>Edit</DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteStudent(s.id)}>Delete</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {filteredStudents.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No students found</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  // ===== COURSES =====
  const renderCourses = () => (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setCrsForm(emptyCrs); setShowCourse(true); }} data-testid="add-course-btn"><Plus className="h-4 w-4 mr-2" />Add Course</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => (
          <Card key={c.id}><CardContent className="p-5">
            <div className="flex items-start justify-between mb-2">
              <div><Badge variant="outline" className="font-mono text-xs mb-1">{c.code}</Badge><p className="font-semibold">{c.name}</p></div>
              {c.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge variant="outline">Inactive</Badge>}
            </div>
            {c.department && <p className="text-xs text-muted-foreground mb-2">{c.department}</p>}
            <div className="grid grid-cols-2 gap-2 text-sm mt-3 pt-3 border-t">
              <div><p className="text-xs text-muted-foreground">Tuition</p><p className="font-mono font-semibold">{fmt(c.fee_amount)}</p></div>
              <div><p className="text-xs text-muted-foreground">Registration</p><p className="font-mono">{fmt(c.registration_fee)}</p></div>
              <div><p className="text-xs text-muted-foreground">Materials</p><p className="font-mono">{fmt(c.material_fee)}</p></div>
              <div><p className="text-xs text-muted-foreground">Total</p><p className="font-mono font-semibold text-primary">{fmt(c.total_fee)}</p></div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs text-muted-foreground">
              <span>{c.duration_months} months | {c.credits} credits</span>
              <span>{c.enrolled_students || 0} enrolled</span>
            </div>
          </CardContent></Card>
        ))}
        {courses.length === 0 && <Card className="col-span-full"><CardContent className="p-12 text-center text-muted-foreground">No courses yet</CardContent></Card>}
      </div>
    </div>
  );

  // ===== ENROLLMENTS =====
  const renderEnrollments = () => (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setEnrForm(emptyEnr); setShowEnroll(true); }} data-testid="enroll-btn"><ClipboardList className="h-4 w-4 mr-2" />Enroll Student</Button></div>
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Course</TableHead><TableHead>Enrolled</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Fee</TableHead></TableRow></TableHeader>
        <TableBody>
          {enrollments.map(e => (
            <TableRow key={e.id}>
              <TableCell><div><p className="font-medium text-sm">{e.student_name}</p><p className="text-xs text-muted-foreground">{e.student_number}</p></div></TableCell>
              <TableCell><div><p className="text-sm">{e.course_name}</p><p className="text-xs text-muted-foreground">{e.course_code}</p></div></TableCell>
              <TableCell className="text-sm">{e.enrollment_date}</TableCell>
              <TableCell><Badge className={STATUS_COLORS[e.status] || 'bg-slate-100'}>{e.status}</Badge></TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(e.course_fee)}</TableCell>
            </TableRow>
          ))}
          {enrollments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No enrollments</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  // ===== INVOICES =====
  const renderInvoices = () => (
    <div className="space-y-4">
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Student</TableHead><TableHead>Course</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead>Due Date</TableHead></TableRow></TableHeader>
        <TableBody>
          {invoices.map(inv => (
            <TableRow key={inv.id}>
              <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
              <TableCell><div><p className="text-sm font-medium">{inv.student_name}</p><p className="text-xs text-muted-foreground">{inv.student_number}</p></div></TableCell>
              <TableCell className="text-sm">{inv.course_name || '-'}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.total)}</TableCell>
              <TableCell className="text-right font-mono text-sm text-emerald-600">{fmt(inv.amount_paid)}</TableCell>
              <TableCell className="text-right font-mono text-sm"><span className={inv.balance > 0 ? 'text-red-600' : ''}>{fmt(inv.balance)}</span></TableCell>
              <TableCell><Badge className={INV_COLORS[inv.status] || 'bg-slate-100'}>{inv.status}</Badge></TableCell>
              <TableCell className="text-sm">{inv.due_date || '-'}</TableCell>
            </TableRow>
          ))}
          {invoices.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No invoices</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  // ===== PAYMENTS =====
  const renderPayments = () => (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setPayForm(emptyPay); setShowPayment(true); }} data-testid="record-payment-btn"><CreditCard className="h-4 w-4 mr-2" />Record Payment</Button></div>
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Invoice #</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead></TableRow></TableHeader>
        <TableBody>
          {payments.map(p => (
            <TableRow key={p.id}>
              <TableCell className="text-sm">{p.payment_date}</TableCell>
              <TableCell className="text-sm font-medium">{p.student_name || '-'}</TableCell>
              <TableCell className="font-mono text-sm">{p.invoice_number || '-'}</TableCell>
              <TableCell className="text-right font-mono text-sm text-emerald-600">{fmt(p.amount)}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs uppercase">{p.method}</Badge></TableCell>
              <TableCell className="text-sm">{p.reference || '-'}</TableCell>
            </TableRow>
          ))}
          {payments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No payments recorded</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="student-billing-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-slate-900">Student Billing</h1><p className="text-muted-foreground mt-1">Enrollment, fees & revenue intelligence</p></div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="sb-dashboard-tab"><BarChart3 className="h-3 w-3 mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="students" data-testid="sb-students-tab"><Users className="h-3 w-3 mr-1" />Students ({students.length})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="sb-courses-tab"><BookOpen className="h-3 w-3 mr-1" />Courses ({courses.length})</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="sb-enrollments-tab"><ClipboardList className="h-3 w-3 mr-1" />Enrollments</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="sb-invoices-tab"><Receipt className="h-3 w-3 mr-1" />Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="sb-payments-tab"><CreditCard className="h-3 w-3 mr-1" />Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard">{renderDashboard()}</TabsContent>
        <TabsContent value="students">{renderStudents()}</TabsContent>
        <TabsContent value="courses">{renderCourses()}</TabsContent>
        <TabsContent value="enrollments">{renderEnrollments()}</TabsContent>
        <TabsContent value="invoices">{renderInvoices()}</TabsContent>
        <TabsContent value="payments">{renderPayments()}</TabsContent>
      </Tabs>

      {/* Student Dialog */}
      <Dialog open={showStudent} onOpenChange={setShowStudent}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{editStudent ? 'Edit Student' : 'Add Student'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>First Name *</Label><Input value={stuForm.first_name} onChange={e => setStuForm({...stuForm, first_name: e.target.value})} data-testid="stu-first-name" /></div>
              <div className="space-y-1"><Label>Last Name *</Label><Input value={stuForm.last_name} onChange={e => setStuForm({...stuForm, last_name: e.target.value})} data-testid="stu-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={stuForm.email} onChange={e => setStuForm({...stuForm, email: e.target.value})} /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={stuForm.phone} onChange={e => setStuForm({...stuForm, phone: e.target.value})} placeholder="+27..." /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>ID Number</Label><Input value={stuForm.id_number} onChange={e => setStuForm({...stuForm, id_number: e.target.value})} /></div>
              <div className="space-y-1"><Label>Date of Birth</Label><Input type="date" value={stuForm.date_of_birth} onChange={e => setStuForm({...stuForm, date_of_birth: e.target.value})} /></div>
              <div className="space-y-1"><Label>Gender</Label>
                <Select value={stuForm.gender} onValueChange={v => setStuForm({...stuForm, gender: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Department</Label><Input value={stuForm.department} onChange={e => setStuForm({...stuForm, department: e.target.value})} /></div>
              <div className="space-y-1"><Label>Program</Label><Input value={stuForm.program} onChange={e => setStuForm({...stuForm, program: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Province</Label>
                <Select value={stuForm.province} onValueChange={v => setStuForm({...stuForm, province: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Enrollment Date</Label><Input type="date" value={stuForm.enrollment_date} onChange={e => setStuForm({...stuForm, enrollment_date: e.target.value})} /></div>
            </div>
            <div className="border-t pt-3"><h4 className="text-sm font-semibold mb-2">Guardian / Parent</h4></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Guardian Name</Label><Input value={stuForm.guardian_name} onChange={e => setStuForm({...stuForm, guardian_name: e.target.value})} /></div>
              <div className="space-y-1"><Label>Guardian Phone</Label><Input value={stuForm.guardian_phone} onChange={e => setStuForm({...stuForm, guardian_phone: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowStudent(false)}>Cancel</Button><Button onClick={handleCreateStudent} data-testid="save-student-btn">{editStudent ? 'Update' : 'Add Student'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Dialog */}
      <Dialog open={showCourse} onOpenChange={setShowCourse}>
        <DialogContent><DialogHeader><DialogTitle>Add Course</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Code *</Label><Input value={crsForm.code} onChange={e => setCrsForm({...crsForm, code: e.target.value})} placeholder="BSC-IT" data-testid="course-code" /></div>
              <div className="space-y-1"><Label>Name *</Label><Input value={crsForm.name} onChange={e => setCrsForm({...crsForm, name: e.target.value})} data-testid="course-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Department</Label><Input value={crsForm.department} onChange={e => setCrsForm({...crsForm, department: e.target.value})} /></div>
              <div className="space-y-1"><Label>Duration (months)</Label><Input type="number" value={crsForm.duration_months} onChange={e => setCrsForm({...crsForm, duration_months: parseInt(e.target.value) || 0})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Tuition Fee (R)</Label><Input type="number" value={crsForm.fee_amount} onChange={e => setCrsForm({...crsForm, fee_amount: parseFloat(e.target.value) || 0})} /></div>
              <div className="space-y-1"><Label>Registration (R)</Label><Input type="number" value={crsForm.registration_fee} onChange={e => setCrsForm({...crsForm, registration_fee: parseFloat(e.target.value) || 0})} /></div>
              <div className="space-y-1"><Label>Materials (R)</Label><Input type="number" value={crsForm.material_fee} onChange={e => setCrsForm({...crsForm, material_fee: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={crsForm.description} onChange={e => setCrsForm({...crsForm, description: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCourse(false)}>Cancel</Button><Button onClick={handleCreateCourse} data-testid="save-course-btn">Create Course</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollment Dialog */}
      <Dialog open={showEnroll} onOpenChange={setShowEnroll}>
        <DialogContent><DialogHeader><DialogTitle>Enroll Student</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Student *</Label>
              <Select value={enrForm.student_id} onValueChange={v => setEnrForm({...enrForm, student_id: v})}><SelectTrigger data-testid="enroll-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_number})</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>Course *</Label>
              <Select value={enrForm.course_id} onValueChange={v => setEnrForm({...enrForm, course_id: v})}><SelectTrigger data-testid="enroll-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code}) - {fmt(c.total_fee)}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>Enrollment Date</Label><Input type="date" value={enrForm.enrollment_date} onChange={e => setEnrForm({...enrForm, enrollment_date: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Scholarship (R)</Label><Input type="number" value={enrForm.scholarship_amount} onChange={e => setEnrForm({...enrForm, scholarship_amount: parseFloat(e.target.value) || 0})} /></div>
              <div className="space-y-1"><Label>Discount (%)</Label><Input type="number" value={enrForm.discount_percent} onChange={e => setEnrForm({...enrForm, discount_percent: parseFloat(e.target.value) || 0})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEnroll(false)}>Cancel</Button><Button onClick={handleEnroll} data-testid="submit-enroll-btn">Enroll & Generate Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent><DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Invoice *</Label>
              <Select value={payForm.invoice_id} onValueChange={v => { const inv = invoices.find(i => i.id === v); setPayForm({...payForm, invoice_id: v, student_id: inv?.student_id || ''}); }}>
                <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>{invoices.filter(i => i.balance > 0).map(i => <SelectItem key={i.id} value={i.id}>{i.invoice_number} - {i.student_name} (Balance: {fmt(i.balance)})</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Amount (R) *</Label><Input type="number" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: parseFloat(e.target.value) || 0})} data-testid="payment-amount" /></div>
              <div className="space-y-1"><Label>Method</Label>
                <Select value={payForm.method} onValueChange={v => setPayForm({...payForm, method: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="eft">EFT</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="debit_order">Debit Order</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Reference</Label><Input value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} /></div>
              <div className="space-y-1"><Label>Date</Label><Input type="date" value={payForm.payment_date} onChange={e => setPayForm({...payForm, payment_date: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button><Button onClick={handlePayment} data-testid="submit-payment-btn">Record Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentBillingPage;
